import { useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { useLiveQuery } from 'dexie-react-hooks';
import { CheckCircle2, ChevronRight, RotateCcw } from 'lucide-react';
import { HelpBubble } from '@/help/HelpBubble';
import { AREA_LABEL, AREA_SECTIONS, areaOfSection, completeRecount, recountDay, recountStillPossible } from '@/data/nights';
import { rt } from '@/data/runtime';
import { formatBusinessDate } from '@/domain/businessDate';
import { AREAS, type AreaId, type BusinessDate, type CountEntry } from '@/domain/types';
import { Button, Card, CardTitle, cn } from '@/ui/core';
import { useToast } from '@/ui/overlay';
import { useRecountDay } from '@/app/hooks';

export interface RecountTarget {
  date: BusinessDate;
  area: AreaId;
  itemId: string;
}

/** Where a recount is done: the area's list, scrolled to the drink. */
export const recountPath = (t: RecountTarget) => `/count/${t.date}/${t.area}?item=${t.itemId}`;

/**
 * Recounts still waiting, in walking order. Only while recounts are open (until 4 p.m. the
 * day after the count at Red's): after that the stock has changed, so an older one is left for
 * a manager (see `recountStillPossible`).
 */
export async function waitingRecounts(oldestRecount?: BusinessDate): Promise<{ entry: CountEntry; area: AreaId; name: string }[]> {
  const db = rt().db;
  const oldest = oldestRecount ?? (await recountDay());
  const entries = (await db.counts.where('recountRequestedAt').above('').toArray()).filter((e) => recountStillPossible(e.date, oldest));
  const items = new Map((await db.items.toArray()).map((i) => [i.id, i]));
  const out: { entry: CountEntry; area: AreaId; name: string }[] = [];
  const seen = new Set<string>();
  for (const e of entries) {
    const night = await db.nights.get(e.date);
    if (night?.status === 'approved') continue;
    // One line per list the drink is on (a beer can be on both beer lists).
    const key = `${e.date}|${e.itemId}|${areaOfSection(e.section)}`;
    if (seen.has(key)) continue;
    seen.add(key);
    out.push({ entry: e, area: areaOfSection(e.section), name: items.get(e.itemId)?.name ?? 'A drink' });
  }
  const order = (e: { entry: CountEntry; area: AreaId }) => AREAS.indexOf(e.area) * 1000 + (items.get(e.entry.itemId)?.rows[e.entry.section] ?? 0);
  return out.sort((a, b) => (a.entry.date < b.entry.date ? -1 : a.entry.date > b.entry.date ? 1 : order(a) - order(b)));
}

export async function nextRecount(): Promise<RecountTarget | null> {
  const first = (await waitingRecounts())[0];
  return first ? { date: first.entry.date, area: first.area, itemId: first.entry.itemId } : null;
}

/** "Please count these again" on the staff home screen. Renders nothing when there are none. */
export function RecountList({ className }: { className?: string }) {
  // Re-read when the recount time passes too, so a tablet left on the home screen drops them.
  const oldest = useRecountDay();
  const list = useLiveQuery(() => waitingRecounts(oldest), [oldest], []);
  if (list.length === 0) return null;
  return (
    <Card className={cn('border-2 border-warn bg-warn-soft', className)}>
      <CardTitle>
        <span className="flex items-center gap-2 text-xl">
          <RotateCcw className="size-6 text-warn" aria-hidden /> Please count these again
          <HelpBubble topic="recounts" size="sm" />
        </span>
      </CardTitle>
      <ul className="flex flex-col gap-2">
        {list.map(({ entry, area, name }) => (
          <li key={entry.id}>
            <Link to={recountPath({ date: entry.date, area, itemId: entry.itemId })} className="flex min-h-16 items-center gap-3 rounded-2xl border-2 border-warn/60 bg-surface px-4 hover:border-warn">
              <span className="min-w-0 flex-1">
                <span className="block text-lg font-bold">{name}</span>
                <span className="block text-sm text-muted">
                  {AREA_LABEL[area]} · {formatBusinessDate(entry.date)}
                  {entry.recountNote && <> · “{entry.recountNote}”</>}
                </span>
              </span>
              <ChevronRight className="size-6 text-muted" aria-hidden />
            </Link>
          </li>
        ))}
      </ul>
    </Card>
  );
}

/**
 * "A manager asked you to count this again", with one big button to say it's done. It saves
 * any number still being typed first, then goes to the next recount, or home when none are
 * left, so nothing is left hanging on the home screen.
 */
export function RecountDone({ date, itemId, area, flush, note, compact }: { date: BusinessDate; itemId: string; area: AreaId; flush: () => Promise<void>; note?: string; compact?: boolean }) {
  const navigate = useNavigate();
  const toast = useToast();
  const [busy, setBusy] = useState(false);
  const done = async () => {
    setBusy(true);
    try {
      await flush();
      await completeRecount(date, itemId, AREA_SECTIONS[area]);
      const next = await nextRecount();
      if (next) {
        toast.show('Recount saved. Here’s the next one.');
        navigate(recountPath(next));
      } else {
        toast.show('All recounts are done. Thank you!');
        navigate('/');
      }
    } catch (err) {
      toast.error(err);
      setBusy(false);
    }
  };
  return (
    <div className={cn('flex flex-wrap items-center gap-3 rounded-2xl border-2 border-warn bg-warn-soft', compact ? 'w-full p-2 pl-3' : 'p-4')}>
      <RotateCcw className={cn('shrink-0 text-warn', compact ? 'size-5' : 'size-7')} aria-hidden />
      <div className="min-w-48 flex-1 text-text">
        <p className={cn('flex items-center gap-1 font-bold', compact ? 'text-base' : 'text-lg md:text-xl')}>
          A manager asked you to count this again <HelpBubble topic="recounts" size="sm" />
        </p>
        {note && <p className="text-sm">“{note}”</p>}
        {!compact && <p className="text-base">Count it, change the number if it’s different, then tap the green button. If it was right, just tap it.</p>}
      </div>
      <Button size={compact ? 'lg' : 'xl'} variant="ok" busy={busy} onClick={done} icon={<CheckCircle2 className={compact ? 'size-5' : 'size-6'} aria-hidden />}>
        Done — I counted it again
      </Button>
    </div>
  );
}
